import React, { useState, useEffect } from "react";
import ObfuscatedEmail from "../components/ObfuscatedEmail";
import {
  ChevronDown,
  ChevronUp,
  Info,
  Users,
  FlaskConical,
  Sparkles,
  ClipboardList,
  ListChecks,
  Rocket,
  Award,
  Clock,
  UserCog,
  ClipboardCheck,
  RefreshCw,
  Database,
  PlayCircle,
  LifeBuoy,
} from "lucide-react";
import { highlightBrand } from "../utils/brandUtils";

const FAQPage: React.FC = () => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const [showGoToTop, setShowGoToTop] = useState(false);

  // Get all FAQ items to build the index
  const faqItems = [
    { id: "what-is-lab-zer0", title: "What is LAB ZER0?" },
    { id: "who-is-lab-zer0-for", title: "Who is LAB ZER0 for?" },
    { id: "wet-and-dry-labs", title: "What are wet and dry labs?" },
    {
      id: "what-makes-lab-zer0-different",
      title: "What makes LAB ZER0 different?",
    },
    { id: "subscription-work", title: "How does the subscription work?" },
    { id: "subscription-includes", title: "Each subscription includes:" },
    { id: "available-now", title: "What's available now?" },
    { id: "certification-work", title: "How does certification work?" },
    {
      id: "certification-time",
      title: "How long does it take to get certified?",
    },
    { id: "platform-admin", title: "Who should be the platform admin?" },
    { id: "auditing-work", title: "How does auditing work?" },
    { id: "platform-updated", title: "How up to date is the platform?" },
    { id: "data-ownership", title: "Who owns the data we enter?" },
    { id: "get-started", title: "How do we get started?" },
    { id: "were-here-to-help", title: "We're Here to Help" },
  ];



  // Resolve an appropriate lucide icon for each FAQ section
  const iconFor = (id: string) => {
    const commonProps = {
      size: 16,
      className:
        "text-emerald-600 group-hover:text-emerald-700 transition-colors",
    } as const;
    switch (id) {
      case "what-is-lab-zer0":
        return <Info {...commonProps} />;
      case "who-is-lab-zer0-for":
        return <Users {...commonProps} />;
      case "wet-and-dry-labs":
        return <FlaskConical {...commonProps} />;
      case "what-makes-lab-zer0-different":
        return <Sparkles {...commonProps} />;
      case "subscription-work":
        return <ClipboardList {...commonProps} />;
      case "subscription-includes":
        return <ListChecks {...commonProps} />;
      case "available-now":
        return <Rocket {...commonProps} />;
      case "certification-work":
        return <Award {...commonProps} />;
      case "certification-time":
        return <Clock {...commonProps} />;
      case "platform-admin":
        return <UserCog {...commonProps} />;
      case "auditing-work":
        return <ClipboardCheck {...commonProps} />;
      case "platform-updated":
        return <RefreshCw {...commonProps} />;
      case "data-ownership":
        return <Database {...commonProps} />;
      case "get-started":
        return <PlayCircle {...commonProps} />;
      case "were-here-to-help":
        return <LifeBuoy {...commonProps} />;
      default:
        return <Info {...commonProps} />;
    }
  };

  const toggleItem = (id: string) => {
    const isOpen = openItems.has(id);
    const newOpenItems = new Set<string>();

    if (!isOpen) {
      newOpenItems.add(id);
      setOpenItems(newOpenItems);

      // Scroll after short delay (wait for content to render)
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const nav = document.querySelector(
            'nav[aria-label="FAQ index"]'
          ) as HTMLElement | null;
          const navHeight = nav?.offsetHeight ?? 0;
          const extraOffset = 24; // add extra space dynamically
          const y =
            element.getBoundingClientRect().top +
            window.pageYOffset -
            navHeight -
            extraOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 150);
    } else {
      setOpenItems(newOpenItems); // close it if already open
    }
  };

  const scrollToSection = (id: string) => {
    // Open the selected FAQ item
    setOpenItems(new Set([id]));

    // Wait for the layout to settle, then measure and scroll
    requestAnimationFrame(() => {
      const element = document.getElementById(id);
      if (!element) return;
      const nav = document.querySelector(
        'nav[aria-label="FAQ index"]'
      ) as HTMLElement | null;
      const navHeight = nav?.offsetHeight ?? 0;
      const extraOffset = 24; // consistent extra spacing

      // Also give React time to render the expanded content for the selected item
      setTimeout(() => {
        const y =
          element.getBoundingClientRect().top +
          window.pageYOffset -
          navHeight -
          extraOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }, 100);
    });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle scroll to show/hide Go To Top button
  useEffect(() => {
    const handleScroll = () => {
      const nav = document.querySelector(
        'nav[aria-label="FAQ index"]'
      ) as HTMLElement | null;
      if (nav) {
        const navRect = nav.getBoundingClientRect();
        // Show button when index is out of viewport (top is above screen)
        setShowGoToTop(navRect.bottom < 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section className="secondary-page">
      <div className="max-w-6xl mx-auto">
        <h1>FAQ</h1>

        {/* Static Index */}
        <div className="mb-8 sm:mb-12 lg:mb-16">
          <nav
            role="navigation"
            aria-label="FAQ index"
            className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-xl mb-6 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4">
              <h2 className="text-xl font-bold text-white m-0">
                Quick Navigation
              </h2>
            </div>
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {faqItems.map((item) => (
                  <div key={item.id} className="text-emerald-600">
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-emerald-50 hover:text-emerald-700 transition-all duration-200 !text-lg border-l-2 border-transparent hover:border-emerald-500 group cursor-pointer flex gap-3"
                    >
                      <span className="inline-flex items-start gap-2 mt-[6px]">
                        {iconFor(item.id)}
                      </span>
                      <span className="text-black group-hover:text-emerald-600 transition-all duration-200">
                        {highlightBrand(item.title)}
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </nav>
        </div>

        {/* FAQ Items */}
        <div className="space-y-6">
          <div
            id="what-is-lab-zer0"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("what-is-lab-zer0")}
            >
              <span>{highlightBrand("What is LAB ZER0?")}</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("what-is-lab-zer0") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("what-is-lab-zer0") && (
              <div className="l0-section-text mt-4">
                <p>
                  We are a Australian social enterprise helping labs make
                  measurable, achievable changes to reduce their environmental
                  impact.
                </p>
                <p>
                  We know research is resource intensive -{" "}
                  <b className="l0-emphasis">
                    high energy use, lots of single-use plastics, large water
                    demand, and heavy data storage needs
                  </b>
                  . Our online platform breaks these challenges into small,
                  clear actions that reduce mental burden and make sustainable
                  lab operations easier to manage.
                </p>
              </div>
            )}
          </div>

          <div
            id="who-is-lab-zer0-for"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("who-is-lab-zer0-for")}
            >
              <span>{highlightBrand("Who is LAB ZER0 for?")}</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("who-is-lab-zer0-for") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("who-is-lab-zer0-for") && (
              <div className="l0-section-text mt-4">
                <p>Our platform is designed for:</p>
                <ul className="l0-list">
                  <li>
                    <b className="l0-emphasis">Academic wet labs</b> –
                    chemistry, biology, and medical research
                  </li>
                  <li>
                    <b className="l0-emphasis">Data-driven dry labs</b> –
                    computational biology, bioinformatics, data-modelling
                  </li>
                  <li>
                    <b className="l0-emphasis">Industry R&D labs</b> –
                    pharmaceuticals, product development, testing facilities
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div
            id="wet-and-dry-labs"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("wet-and-dry-labs")}
            >
              <span>What are wet and dry labs?</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("wet-and-dry-labs") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("wet-and-dry-labs") && (
              <div className="l0-section-text mt-4">
                <ul className="l0-list">
                  <li>
                    <b className="l0-emphasis">Wet labs </b>
                    are physical research spaces where experiments involve
                    chemicals, biological matter, or liquids. They often use
                    specialised equipment, ventilation, and consumables like
                    plastics, glassware, and reagents.
                  </li>
                  <li>
                    <b className="l0-emphasis">Dry labs </b>
                    focus on computational or theoretical research, such as data
                    analysis, modelling, or simulations - where most work
                    happens on computers and servers.
                  </li>
                </ul>
                <p>
                  {highlightBrand(
                    "Whether your team works at the bench, on the cloud, or both, LAB ZER0 supports both wet and dry laboratories!"
                  )}
                </p>
              </div>
            )}
          </div>

          <div
            id="what-makes-lab-zer0-different"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("what-makes-lab-zer0-different")}
            >
              <span>{highlightBrand("What makes LAB ZER0 different?")}</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("what-makes-lab-zer0-different") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("what-makes-lab-zer0-different") && (
              <div className="l0-section-text mt-4">
                <p>We have:</p>
                <ul className="l0-list">
                  <li>
                    <b className="l0-emphasis">Data-driven results</b> - not
                    just checklist scores; we provide estimate impact reports
                    that track trends in energy, emissions, water, recycling,
                    purchasing, green chemistry, efficient computing and much
                    more!
                  </li>
                  <li>
                    <b className="l0-emphasis">
                      Tailored assessments & checklists
                    </b>{" "}
                    - wet and dry lab checklists (dependent on subscription)
                    with embedded resources, easy to follow and customisable to
                    your organisation's policies.
                  </li>
                  <li>
                    <b className="l0-emphasis">Safety aligned</b> - all actions
                    are designed to meet Australian safety and compliance
                    requirements.
                  </li>
                  <li>
                    <b className="l0-emphasis">Australian-first approach</b> –
                    to make our platform run we partner with local suppliers
                    that prioritise fossil fuel divestment, and publicly report
                    their net zero strategies.
                  </li>
                  <li>
                    <b className="l0-emphasis">Data sovereignty</b> – all data
                    is stored securely on Australian servers.
                  </li>
                  <li>
                    <b className="l0-emphasis">Independently funded</b> – no
                    reliance on grants, so we can respond to your feedback
                    quickly!
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div
            id="subscription-work"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("subscription-work")}
            >
              <span>How does the subscription work?</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("subscription-work") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("subscription-work") && (
              <div className="l0-section-text mt-4">
                <p>Your organisation can subscribe between 1-100 labs to our:</p>
                <ul className="l0-list">
                  <li>
                    <b className="l0-emphasis">Wet lab platform</b> – themes
                    include cold storage efficiency, laboratory waste, green
                    purchasing, chemical use, ventilation, water reduction, and
                    heavy equipment energy use.
                  </li>
                  <li>
                    <b className="l0-emphasis">Dry lab platform</b> – themes
                    include eco-office practices, sustainable data storage,
                    efficient computing, and minimising energy use in IT
                    systems.
                  </li>
                  <li>
                    <b className="l0-emphasis">Both platforms</b> – for a
                    discounted rate!
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div
            id="subscription-includes"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("subscription-includes")}
            >
              <span>Each subscription includes:</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("subscription-includes") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("subscription-includes") && (
              <div className="l0-section-text mt-4">
                <ul className="l0-list">
                  <li>
                    <b className="l0-emphasis">Baseline assessments</b> - record
                    your lab's current energy, water, waste, and emissions use
                    so you can measure real progress over time.
                  </li>
                  <li>
                    <b className="l0-emphasis">Action checklists</b> - tailored
                    to wet or dry labs, with Yes|No|N/A options and embedded
                    resources for guidance; simple to follow and customisable to
                    your organisation's needs.
                  </li>
                  <li>
                    <b className="l0-emphasis">Estimate impact reports</b> –
                    estimate and track savings and improvements, either for
                    individual labs or across your entire organisation!
                  </li>
                  <li>
                    <b className="l0-emphasis">Auditing feature</b> – get expert
                    guided feedback on your progress and certification
                    readiness.
                  </li>
                  <li>
                    <b className="l0-emphasis">Practical resources</b> – access
                    ready-to-use templates, guides, and tools to help your team
                    keep improving.
                  </li>
                  <li>
                    <b className="l0-emphasis">Customer support</b> – direct
                    help from our team to troubleshoot issues, answer questions,
                    and guide you through the platform.
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div
            id="available-now"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("available-now")}
            >
              <span>What's available now?</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("available-now") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("available-now") && (
              <div className="l0-section-text mt-4">
                <ul className="l0-list">
                  <li>12-month subscription</li>
                  <li>Includes access to Bronze, Silver, and Gold certifications</li>
                </ul>
              </div>
            )}
          </div>

          <div
            id="certification-work"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("certification-work")}
            >
              <span>How does certification work?</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("certification-work") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("certification-work") && (
              <div className="l0-section-text mt-4">
                You progress through Bronze, Silver, and Gold levels by
                completing baseline and savings assessments, taking action
                through checklists and guides, and improve your lab's
                sustainability performance. Each level builds on the last,
                rewarding ongoing improvement.
              </div>
            )}
          </div>

          <div
            id="certification-time"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("certification-time")}
            >
              <span>How long does it take to get certified?</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("certification-time") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("certification-time") && (
              <div className="l0-section-text mt-4">
                Every lab's journey is different. How quickly you get certified
                depends on things like how involved your team is, the size of
                your lab, and whether you're already working sustainably. Some
                labs move through their first level quickly, while others take a
                bit more time to make changes. We anticipate for Bronze
                certification to be reached within 3–6 months, with higher
                levels following as you keep building on your progress.
              </div>
            )}
          </div>

          <div
            id="platform-admin"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("platform-admin")}
            >
              <span>Who should be the platform admin?</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("platform-admin") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("platform-admin") && (
              <div className="l0-section-text mt-4">
                <p>
                  We recommend choosing someone who has an operational role or
                  understands your laboratory structure at a higher level – for
                  example, a lab manager, operations coordinator, or
                  sustainability lead.
                </p>
                <p>
                  You can also have more than one admin to share the role. This
                  helps spread the workload and ensures there's always someone
                  available to manage invites, oversee progress, and support
                  labs using the platform.
                </p>
              </div>
            )}
          </div>

          <div
            id="auditing-work"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("auditing-work")}
            >
              <span>How does auditing work?</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("auditing-work") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("auditing-work") && (
              <div className="l0-section-text mt-4">
                <p>
                  When a lab submits for certification, the organisation's admin
                  assigns a user from another lab to act as the auditor. The
                  auditor is given clear guidance and an easy-to-follow auditors
                  checklist so they know exactly what to look for. This approach
                  keeps the process simple, reduces mental load, and makes
                  reviews quick and efficient. It's also an effective way for us
                  to keep the pricing model low, while giving individuals the
                  chance to support each other's progress.
                </p>
                <p>
                  Our peer-auditing model also reinforces social norm modelling,
                  which is essential for creating lasting behaviour change
                  across the whole organisation, not just within a single lab.
                </p>
              </div>
            )}
          </div>

          <div
            id="platform-updated"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("platform-updated")}
            >
              <span>How up to date is the platform?</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("platform-updated") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("platform-updated") && (
              <div className="l0-section-text mt-4">
                <p>
                  We've built the platform so the checklists and assessments
                  stay relevant without needing constant changes. But we're
                  always on the lookout for new ways to make it even better!
                  Whenever fresh research or important updates in Australian
                  sustainability come along, we'll review and add them if
                  they'll genuinely help our users.
                </p>
                <p>
                  If you spot something new or have non-confidential, public
                  resources to share, we'd love to hear from you, just send us
                  an email at <ObfuscatedEmail email="hello@labzero.eco" />.
                  It's a great way to help keep the platform useful for
                  everyone.
                </p>
              </div>
            )}
          </div>

          <div
            id="data-ownership"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("data-ownership")}
            >
              <span>Who owns the data we enter?</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("data-ownership") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("data-ownership") && (
              <div className="l0-section-text mt-4">
                <p>
                  {highlightBrand(
                    "Your organisation retains ownership of all data. LAB ZER0 securely stores it in Australia and uses it only to generate your impact reports and certificates and anonymously share aggregate results to help the Australian research and laboratory sector!"
                  )}
                </p>
              </div>
            )}
          </div>

          <div
            id="get-started"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("get-started")}
            >
              <span>How do we get started?</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("get-started") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("get-started") && (
              <div className="l0-section-text mt-4">
                <ul className="l0-list-nr">
                  <li>
                    Request to view our vendor pack by emailing{" "}
                    <ObfuscatedEmail email="hello@labzero.eco" />
                  </li>
                  <li>
                    Choose your subscription type - wet lab, dry lab, or
                    combined.
                  </li>
                  <li>
                    Request your institute referral code from{" "}
                    <ObfuscatedEmail email="hello@labzero.eco" />
                  </li>
                  <li>Start your onboarding process with our team</li>
                </ul>
              </div>
            )}
          </div>

          <div
            id="were-here-to-help"
            className="l0-faq-item !pb-3 border px-4 shadow-lg rounded-3xl pt-6 scroll-mt-24"
          >
            <h2
              className="l0-section-heading cursor-pointer flex justify-between items-center"
              onClick={() => toggleItem("were-here-to-help")}
            >
              <span>We're Here to Help</span>
              <span
                className={`ml-2 transition-transform duration-300 ${openItems.has("were-here-to-help") ? "rotate-180" : "rotate-0"}`}
              >
                <ChevronDown />
              </span>
            </h2>
            {openItems.has("were-here-to-help") && (
              <div className="l0-section-text mt-4">
                <p>
                  If you ever run into issues while using the platform, you can
                  quickly report a bug right from within the platform. Our
                  customer support team is also just an email away to answer
                  questions or help you troubleshoot.
                </p>
                <p>
                  {highlightBrand(
                    "We also run a training session for admins and LAB ZER0 Champions so you feel confident using the platform and supporting your labs from day one!"
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Go To Top Button */}
      {showGoToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-[160px] right-6 z-30 text-white bg-teal-600 p-3 rounded-full shadow-lg hover:bg-teal-600/80 transition-all duration-300 ease-in-out cursor-pointer"
          aria-label="Go to top"
        >
          <ChevronUp size={24} />
        </button>
      )}
    </section>
  );
};

FAQPage.displayName = "FAQPage";

export default FAQPage;
